/**
 * Diễn đàn thảo luận theo môn (plan 2026-10-03 §2) — `/api/forum/*`,
 * hàng đợi báo cáo `/api/admin/forum/reports/`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./client";

export interface ForumAuthor {
  id: number | null;
  name: string;
  is_staff: boolean;
}

export interface ForumThreadSummary {
  id: number;
  title: string;
  excerpt: string;
  author: ForumAuthor;
  is_question: boolean;
  is_pinned: boolean;
  is_locked: boolean;
  is_answered: boolean;
  reply_count: number;
  last_activity_at: string;
  created_at: string;
  is_deleted: boolean;
  deleted_label: string;
  is_subscribed: boolean;
}

export interface ForumPost {
  id: number;
  parent: number | null;
  author: ForumAuthor;
  body: string;
  removed_body: string;
  is_instructor_reply: boolean;
  is_accepted: boolean;
  created_at: string;
  edited_at: string | null;
  is_deleted: boolean;
  deleted_label: string;
  deleted_reason: string;
  can_edit: boolean;
  can_delete: boolean;
}

export interface ForumThreadDetail extends ForumThreadSummary {
  body: string;
  removed_body: string;
  deleted_reason: string;
  edited_at: string | null;
  course: { slug: string; code: string; name: string };
  accepted_post: number | null;
  posts: ForumPost[];
  permissions: {
    can_reply: boolean;
    can_moderate: boolean;
    can_edit: boolean;
    can_delete: boolean;
    can_accept: boolean;
  };
}

export interface ForumThreadList {
  count: number;
  page_size: number;
  results: ForumThreadSummary[];
  course: { slug: string; code: string; name: string };
  permissions: { can_post: boolean; can_moderate: boolean };
}

export type ForumFilter = "all" | "unanswered" | "mine" | "following";

const KEY = ["forum"] as const;

export function useForumThreads(
  slug: string,
  params: { q?: string; filter?: ForumFilter; page?: number },
  enabled = true,
) {
  return useQuery({
    queryKey: [...KEY, "threads", slug, params],
    queryFn: async () =>
      (
        await apiClient.get<ForumThreadList>(`/forum/courses/${slug}/threads/`, {
          params: { q: params.q || undefined, filter: params.filter, page: params.page },
        })
      ).data,
    enabled,
  });
}

export function useForumThread(id: number | null) {
  return useQuery({
    queryKey: [...KEY, "thread", id],
    queryFn: async () => (await apiClient.get<ForumThreadDetail>(`/forum/threads/${id}/`)).data,
    enabled: id != null,
  });
}

function useForumMutation<TVars, TData = unknown>(fn: (v: TVars) => Promise<TData>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export const useCreateThread = (slug: string) =>
  useForumMutation(
    async (v: { title: string; body: string; is_question: boolean }) =>
      (await apiClient.post<ForumThreadDetail>(`/forum/courses/${slug}/threads/`, v)).data,
  );

export const useUpdateThread = () =>
  useForumMutation(
    async ({ id, ...v }: { id: number; title?: string; body?: string; is_question?: boolean }) =>
      (await apiClient.patch<ForumThreadDetail>(`/forum/threads/${id}/`, v)).data,
  );

export const useDeleteThread = () =>
  useForumMutation(async ({ id, reason }: { id: number; reason?: string }) =>
    apiClient.delete(`/forum/threads/${id}/`, { data: { reason } }),
  );

export const useReply = () =>
  useForumMutation(
    async ({ thread, ...v }: { thread: number; body: string; parent?: number }) =>
      (await apiClient.post<ForumPost>(`/forum/threads/${thread}/posts/`, v)).data,
  );

export const useUpdatePost = () =>
  useForumMutation(
    async ({ id, body }: { id: number; body: string }) =>
      (await apiClient.patch<ForumPost>(`/forum/posts/${id}/`, { body })).data,
  );

export const useDeletePost = () =>
  useForumMutation(async ({ id, reason }: { id: number; reason?: string }) =>
    apiClient.delete(`/forum/posts/${id}/`, { data: { reason } }),
  );

export const useModerateThread = () =>
  useForumMutation(
    async ({ id, action }: { id: number; action: "pin" | "unpin" | "lock" | "unlock" }) =>
      (await apiClient.post<ForumThreadDetail>(`/forum/threads/${id}/moderate/`, { action })).data,
  );

export const useAcceptPost = () =>
  useForumMutation(
    async ({ id, post }: { id: number; post: number | null }) =>
      (await apiClient.post<ForumThreadDetail>(`/forum/threads/${id}/accept/`, { post })).data,
  );

export const useSubscribeThread = () =>
  useForumMutation(
    async ({ id, subscribed }: { id: number; subscribed: boolean }) =>
      (await apiClient.post<ForumThreadDetail>(`/forum/threads/${id}/subscribe/`, { subscribed }))
        .data,
  );

export const useReportForum = () =>
  useForumMutation(async (v: { thread: number; post?: number; reason: string }) =>
    apiClient.post("/forum/reports/", v),
  );

// ─── Admin: báo cáo vi phạm ──────────────────────────────────────────────────

export type ForumReportStatus = "open" | "removed" | "dismissed";

export interface ForumReport {
  id: number;
  status: ForumReportStatus;
  status_label: string;
  reason: string;
  reporter: string;
  created_at: string;
  thread: { id: number; title: string; course_code: string; course_slug: string };
  post_id: number | null;
  target_author: string;
  target_body: string;
  target_deleted: boolean;
  handled_by: string;
  handled_at: string | null;
  resolution_note: string;
}

export function useForumReports(status: ForumReportStatus | "all") {
  return useQuery({
    queryKey: [...KEY, "reports", status],
    queryFn: async () =>
      (await apiClient.get<ForumReport[]>("/admin/forum/reports/", { params: { status } })).data,
  });
}

export const useResolveReport = () =>
  useForumMutation(
    async ({ id, ...v }: { id: number; action: "remove" | "dismiss"; note?: string }) =>
      (await apiClient.post<ForumReport>(`/admin/forum/reports/${id}/resolve/`, v)).data,
  );

export function forumErrorMessage(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: Record<string, unknown> } }).response?.data;
  if (!data) return fallback;
  if (typeof data.detail === "string") return data.detail;
  const first = Object.values(data)[0];
  return Array.isArray(first) ? String(first[0]) : typeof first === "string" ? first : fallback;
}
